import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { contactInfoApi } from '../../services/adminApi';

const ContactInfoList = () => {
  const navigate = useNavigate();
  const [contactInfos, setContactInfos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sorting, setSorting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchContactInfos = async () => {
    try {
      setLoading(true);
      const response = await contactInfoApi.getAll();
      if (response.data.success) {
        const contactInfosData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data?.data || [];
        setContactInfos(contactInfosData);
      } else {
        setContactInfos([]);
      }
    } catch (err) {
      console.error('Contact infos fetch error:', err);
      setError('İletişim bilgileri yüklenirken hata oluştu');
      setContactInfos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactInfos();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu iletişim bilgisini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await contactInfoApi.delete(id);
      await fetchContactInfos();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Silme işlemi sırasında hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSorting(true);
      
      const oldIndex = contactInfos.findIndex(item => item.id === active.id);
      const newIndex = contactInfos.findIndex(item => item.id === over.id);
      
      const newItems = arrayMove(contactInfos, oldIndex, newIndex);
      setContactInfos(newItems);

      try {
        await contactInfoApi.updateSortOrder({
          items: newItems.map((item, index) => ({
            id: item.id,
            sort_order: index + 1
          }))
        });
      } catch (err) {
        console.error('Sort update error:', err);
        await fetchContactInfos();
      } finally {
        setSorting(false);
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'phone': return '📞';
      case 'email': return '📧';
      case 'address': return '📍';
      case 'social': return '🌐';
      default: return '📋';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'phone': return 'Telefon';
      case 'email': return 'E-posta';
      case 'address': return 'Adres';
      case 'social': return 'Sosyal Medya';
      default: return 'Diğer';
    }
  };

  const SortableRow = ({ contactInfo, onDelete }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: contactInfo.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const tableRowStyle = {
      backgroundColor: 'white',
      borderBottom: '1px solid #e5e7eb',
      transition: 'all 0.2s'
    };

    const tableCellStyle = {
      padding: '1rem',
      fontSize: '0.875rem',
      color: '#374151'
    };

    const dragHandleStyle = {
      cursor: 'grab',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      fontSize: '0.875rem',
      fontWeight: 'bold',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '2rem',
      height: '2rem'
    };

    const statusBadgeStyle = {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: contactInfo.is_active ? '#dcfce7' : '#fee2e2',
      color: contactInfo.is_active ? '#166534' : '#dc2626'
    };

    const actionButtonStyle = {
      padding: '0.5rem',
      borderRadius: '0.375rem',
      border: 'none',
      fontSize: '0.75rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginRight: '0.5rem'
    };

    return (
      <tr ref={setNodeRef} style={style} {...attributes}>
        <td style={tableCellStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              {...listeners}
              style={dragHandleStyle} 
              title="Sürükleyerek sıralayın"
            >
              ⋮⋮
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '1rem' }}>{getTypeIcon(contactInfo.type)}</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>
                  {contactInfo.title}
                </span>
                <span style={statusBadgeStyle}>
                  {getTypeLabel(contactInfo.type)}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', maxWidth: '400px' }}>
                {contactInfo.value}
              </div>
            </div>
          </div>
        </td>
        <td style={tableCellStyle}>
          <span style={statusBadgeStyle}>
            {contactInfo.is_active ? 'Aktif' : 'Pasif'}
          </span>
        </td>
        <td style={tableCellStyle}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => navigate(`/admin/contact-info/${contactInfo.id}/edit`)}
              style={{
                ...actionButtonStyle,
                backgroundColor: '#3b82f6',
                color: 'white'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Düzenle
            </button>
            <button
              onClick={() => onDelete(contactInfo.id)}
              style={{
                ...actionButtonStyle,
                backgroundColor: '#dc2626',
                color: 'white'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              Sil
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const headerCardStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    color: 'white',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const addButtonStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const tableStyle = {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f8fafc',
    padding: '1rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    fontSize: '1rem',
    color: '#6b7280'
  };

  const errorStyle = {
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem',
    color: '#dc2626',
    fontSize: '0.875rem'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280'
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '1rem',
            height: '1rem',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          İletişim bilgileri yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={headerCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.875rem', fontWeight: '700' }}>
              İletişim Bilgileri
            </h1>
            <p style={{ margin: 0, opacity: 0.9 }}>
              Telefon, e-posta, adres ve sosyal medya bilgilerini yönetin
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/contact-info/create')}
            style={addButtonStyle}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            ➕ Yeni İletişim Bilgisi
          </button>
        </div>
      </div>

      {error && (
        <div style={errorStyle}>
          ⚠️ {error}
        </div>
      )}

      <div style={tableStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>İletişim Bilgisi</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '120px' }}>Durum</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '200px' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {!contactInfos || contactInfos.length === 0 ? (
              <tr>
                <td colSpan="3" style={emptyStateStyle}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📞</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Henüz iletişim bilgisi yok
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    İlk iletişim bilginizi eklemek için yukarıdaki butona tıklayın
                  </div>
                </td>
              </tr>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={contactInfos && contactInfos.length > 0 ? contactInfos.map(ci => ci.id) : []}
                  strategy={verticalListSortingStrategy}
                >
                  {contactInfos && contactInfos.length > 0 ? contactInfos.map(contactInfo => (
                    <SortableRow
                      key={contactInfo.id}
                      contactInfo={contactInfo}
                      onDelete={handleDelete}
                    />
                  )) : null}
                </SortableContext>
              </DndContext>
            )}
          </tbody>
        </table>
      </div>

      {sorting && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '1rem 2rem',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          zIndex: 1000
        }}>
          Sıralama güncelleniyor...
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ContactInfoList;
